import { Component, ChangeDetectionStrategy, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AvatarModule } from 'primeng/avatar';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { Observable, map } from 'rxjs';
import { ChatStateService } from '../../../services/chat/chat-state.service';
import { ConversationResponse, ChatUser } from '../../../models/chat/chat-api.models';
import { ChatService } from '../../../services/chat/chat.service';

@Component({
  selector: 'app-chat-sidebar',
  standalone: true,
  imports: [CommonModule, FormsModule, AvatarModule, DialogModule, InputTextModule, ButtonModule],
  templateUrl: './chat-sidebar.component.html',
  styleUrls: ['./chat-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatSidebarComponent implements OnInit {
  activeConversation$ = this.state.activeConversation$;
  conversations$ = this.state.conversations$;
  filteredConversations$: Observable<ConversationResponse[]>;
  searchText: string = '';

  // Dialog
  displayNewChat = false;
  chatUsers: ChatUser[] = [];

  constructor(
      private state: ChatStateService,
      private chatService: ChatService,
      private cdr: ChangeDetectorRef
  ) {
      this.filteredConversations$ = this.conversations$; // Initial
  }

  ngOnInit() {
      this.state.loadConversations();
  }

  onSearch() {
      this.filteredConversations$ = this.conversations$.pipe(
          map(list => list.filter(c => c.name.toLowerCase().includes(this.searchText.toLowerCase())))
      );
  }

  select(conv: ConversationResponse) {
      this.state.selectConversation(conv);
  }

  getAvatar(conv: ConversationResponse): string | undefined {
      return (conv.participants?.[0]?.profilePictureUrl) || undefined;
  }

  getInitials(name: string): string {
      if (!name) return 'U';
      return name.charAt(0).toUpperCase();
  }

  userPresence$ = this.state.userPresence$;

  getPresenceObj(presenceMap: Map<string, { presenceStatus: number, lastSeen: string | null }> | null, conv: ConversationResponse): { presenceStatus: number, lastSeen: string | null } | undefined {
      if (!presenceMap || !conv.participants || conv.participants.length === 0) return undefined;
      return presenceMap.get(conv.participants[0].userId.toLowerCase());
  }

  isTyping(convId: number): Observable<boolean> {
      return this.state.typingUsers$.pipe(
          map(map => {
              const set = map.get(convId);
              return !!set && set.size > 0;
          })
      );
  }

  // Dialog Logic
  showNewChatDialog() {
      this.displayNewChat = true;
      this.loadUsers();
  }
  
  loadUsers(term: string = '') {
      this.chatService.getChatUsers(term).subscribe(res => {
          this.chatUsers = (res as any).result || [];
          this.cdr.markForCheck(); // Force update
      });
  }

  onUserSearch(e: any) {
      this.loadUsers(e.target.value);
  }

  startChat(user: ChatUser) {
      this.state.createConversation(user.id);
      this.displayNewChat = false;
  }

  trackById(index: number, item: any): number {
    return item.id;
  }

  formatLastMessage(html: string | null | undefined): string {
      if (!html) return 'No messages yet';
      
      // Infer attachment types from our rendered HTML structure
      if (html.includes('<img')) {
          const text = this.stripHtml(html).trim();
          return text ? `📷 Image, ${text}` : '📷 Image';
      }
      if (html.includes('pi-file') || html.includes('File Attachment')) {
          const text = this.stripHtml(html).replace('File AttachmentClick to open', '').trim();
          return text ? `📄 File, ${text}` : '📄 File';
      }

      return this.stripHtml(html) || 'No messages yet';
  }

  stripHtml(html: string | null | undefined): string {
      if (!html) return '';
      const tmp = document.createElement("DIV");
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || "";
  }
}
